import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export default function Account() {
    const { consumer, refresh, logout } = useAuth();
    const nav = useNavigate();
    const [name, setName] = useState(consumer?.name || "");
    const [email, setEmail] = useState(consumer?.email || "");
    const [lang, setLang] = useState(consumer?.preferred_language || "en-IN");
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.patch("/me", { name, email: email || null, preferred_language: lang });
            await refresh();
            toast.success("Saved");
        } catch {
            toast.error("Could not save");
        } finally {
            setBusy(false);
        }
    };

    const deleteData = async () => {
        if (!confirmDelete) return;
        setBusy(true);
        try {
            await api.delete("/me/data");
            await logout();
            nav("/");
            toast.success("Your data has been deleted.");
        } catch {
            toast.error("Failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Layout>
            <h1 className="display text-[28px] mb-5">Account</h1>

            <div className="speakup-card p-6 space-y-5" data-testid="account-profile-card">
                <div>
                    <div className="text-[12px] uppercase tracking-wider text-[var(--speakup-text-muted)]">Phone</div>
                    <div className="tabular text-[var(--speakup-text-strong)] mt-1">{consumer?.phone}</div>
                </div>
                <Field label="Your name (used in complaint drafts)">
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="account-name-input"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)]"
                    />
                </Field>
                <Field label="Email (optional — for important notices)">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="account-email-input"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)]"
                    />
                </Field>
                <Field label="Preferred language">
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        data-testid="account-lang-select"
                        className="w-full h-11 px-3 rounded-[10px] border border-[var(--speakup-border)] bg-white"
                    >
                        <option value="en-IN">English</option>
                        <option value="hi-IN">Hindi</option>
                        <option value="ta-IN">Tamil</option>
                    </select>
                </Field>
                <div>
                    <button
                        onClick={save}
                        disabled={busy}
                        data-testid="account-save-btn"
                        className="h-12 px-5 rounded-[12px] text-white font-semibold disabled:opacity-60"
                        style={{ background: "var(--speakup-red)" }}
                    >
                        Save changes
                    </button>
                </div>
            </div>

            <div className="mt-6 speakup-card p-6" data-testid="account-privacy-card">
                <div className="display text-[18px]">Privacy & data</div>
                <p className="text-[14px] text-[var(--speakup-text-muted)] mt-1">
                    Voice clips auto-expire in 30 days. You can also wipe everything below — your complaints,
                    voice clips, and account.
                </p>
                <label className="mt-4 flex items-start gap-2 select-none">
                    <input
                        type="checkbox"
                        checked={confirmDelete}
                        onChange={(e) => setConfirmDelete(e.target.checked)}
                        data-testid="account-delete-confirm"
                        className="mt-1 h-5 w-5 accent-[var(--speakup-red)]"
                    />
                    <span className="text-[14px]">I understand this is permanent.</span>
                </label>
                <button
                    onClick={deleteData}
                    disabled={!confirmDelete || busy}
                    data-testid="account-delete-btn"
                    className="mt-3 h-11 px-4 rounded-[10px] font-semibold border-2 disabled:opacity-40"
                    style={{ borderColor: "var(--speakup-red)", color: "var(--speakup-red)" }}
                >
                    Delete my data
                </button>
            </div>

            <div className="mt-6">
                <button
                    onClick={async () => {
                        await logout();
                        nav("/");
                    }}
                    data-testid="account-logout-btn"
                    className="h-11 px-4 rounded-[10px] border border-[var(--speakup-border)]"
                >
                    Sign out
                </button>
            </div>
        </Layout>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[13px] font-semibold text-[var(--speakup-text-strong)] mb-1.5">{label}</div>
            {children}
        </label>
    );
}
